import { useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase/client';
import { useToast } from '@/hooks/useToast';
import { useAuthStore } from '@/store/authStore';
import type { 
  FinishedGoodsReceipt, 
  CreateFGReceiptRequest,
  WorkOrderFGSummary,
  Warehouse 
} from '@/types/finishedGoods';

export function useFinishedGoodsReceipt() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();
  const user = useAuthStore((state) => state.user);

  // Fetch all FG receipts
  const fetchFGReceipts = useCallback(async (filters?: {
    work_order_id?: string;
    warehouse_id?: string;
    qc_status?: string;
    from_date?: string;
    to_date?: string;
  }): Promise<FinishedGoodsReceipt[]> => {
    try {
      setLoading(true);
      setError(null);

      let query = supabase
        .from('finished_goods_receipt')
        .select(`
          *,
          work_order:work_order_id(id, work_order_number, quantity, status),
          warehouse:warehouse_id(id, code, name),
          creator:created_by(id, full_name, email)
        `)
        .order('created_at', { ascending: false });

      if (filters?.work_order_id) {
        query = query.eq('work_order_id', filters.work_order_id);
      }
      if (filters?.warehouse_id) {
        query = query.eq('warehouse_id', filters.warehouse_id);
      }
      if (filters?.qc_status) {
        query = query.eq('qc_status', filters.qc_status);
      }
      if (filters?.from_date) {
        query = query.gte('receipt_date', filters.from_date);
      }
      if (filters?.to_date) {
        query = query.lte('receipt_date', filters.to_date);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;
      return data || [];
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch FG receipts';
      setError(errorMessage);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Get FG receipt by ID
  const getFGReceipt = useCallback(async (id: string): Promise<FinishedGoodsReceipt | null> => {
    try {
      const { data, error: fetchError } = await supabase
        .from('finished_goods_receipt')
        .select(`
          *,
          work_order:work_order_id(id, work_order_number, quantity, status),
          warehouse:warehouse_id(id, code, name),
          creator:created_by(id, full_name, email)
        `)
        .eq('id', id)
        .single();

      if (fetchError) throw fetchError;
      return data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch FG receipt';
      setError(errorMessage);
      return null;
    }
  }, []);

  // Get FG summary for a work order
  const getWorkOrderFGSummary = useCallback(async (workOrderId: string): Promise<WorkOrderFGSummary | null> => {
    try {
      // Get work order details
      const { data: workOrder, error: woError } = await supabase
        .from('work_orders')
        .select('id, work_order_number, quantity, status')
        .eq('id', workOrderId)
        .single();

      if (woError) throw woError;

      // Get all FG receipts for this work order
      const { data: receipts, error: receiptsError } = await supabase
        .from('finished_goods_receipt')
        .select(`
          *,
          warehouse:warehouse_id(id, code, name)
        `)
        .eq('work_order_id', workOrderId)
        .order('created_at', { ascending: false });

      if (receiptsError) throw receiptsError;

      const receivedQty = (receipts || []).reduce((sum, r) => sum + r.quantity, 0);

      return {
        work_order_id: workOrder.id,
        work_order_number: workOrder.work_order_number,
        ordered_qty: workOrder.quantity,
        received_qty: receivedQty,
        pending_qty: Math.max(0, workOrder.quantity - receivedQty),
        receipts: receipts || [],
      };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch WO FG summary';
      setError(errorMessage);
      return null;
    }
  }, []);

  // Create FG receipt
  const createFGReceipt = useCallback(async (request: CreateFGReceiptRequest): Promise<FinishedGoodsReceipt | null> => {
    if (!user?.id) {
      toast({
        title: 'Error',
        description: 'You must be logged in to create FG receipts',
        variant: 'destructive',
      });
      return null;
    }

    try {
      setLoading(true);
      setError(null);

      // Generate receipt number
      const { data: receiptNumber, error: numError } = await supabase
        .rpc('generate_fg_receipt_number');

      if (numError) throw numError;

      // Create the FG receipt
      const { data: receipt, error: insertError } = await supabase
        .from('finished_goods_receipt')
        .insert({
          receipt_number: receiptNumber,
          work_order_id: request.work_order_id,
          item_id: request.item_id,
          item_name: request.item_name,
          quantity: request.quantity,
          uom: request.uom || 'NOS',
          warehouse_id: request.warehouse_id,
          bin_id: request.bin_id,
          qc_status: request.qc_status || 'PENDING',
          qc_passed_qty: request.qc_passed_qty || 0,
          qc_rejected_qty: request.qc_rejected_qty || 0,
          remarks: request.remarks,
          created_by: user.id,
        })
        .select()
        .single();

      if (insertError) throw insertError;

      // Create inventory transaction (stock IN to FG warehouse)
      // Only create if item_id is provided and valid (exists in bom_components)
      if (request.item_id) {
        // Verify the item_id exists in bom_components before creating transaction
        const { data: bomComponent, error: bomError } = await supabase
          .from('bom_components')
          .select('id')
          .eq('id', request.item_id)
          .single();

        if (bomError || !bomComponent) {
          console.warn('Item ID not found in bom_components, skipping inventory transaction:', request.item_id);
        } else {
          // Use 'PRODUCTION-RECEIPT' as it's the valid txn_type for FG receipts from production
          const { error: txError } = await supabase
            .from('inventory_tx')
            .insert({
              txn_type: 'PRODUCTION-RECEIPT',
              item_id: request.item_id,
              qty: request.quantity,
              uom: request.uom || 'NOS',
              wh_to: request.warehouse_id,
              bin_to: request.bin_id,
              ref_table: 'finished_goods_receipt',
              ref_id: receipt.id,
              note: `FG Receipt ${receiptNumber} from Work Order`,
              created_by: user.id,
            });

          if (txError) {
            console.error('Failed to create inventory transaction:', txError);
            // Don't fail the whole operation, just log the error
          }
        }
      } else {
        console.warn('No item_id provided for FG receipt, skipping inventory transaction');
      }

      // Update work order FG received qty
      const { data: currentWO } = await supabase
        .from('work_orders')
        .select('fg_received_qty')
        .eq('id', request.work_order_id)
        .single();

      const newReceivedQty = (currentWO?.fg_received_qty || 0) + request.quantity;

      await supabase
        .from('work_orders')
        .update({
          fg_received_qty: newReceivedQty,
          fg_warehouse_id: request.warehouse_id,
          fg_received_at: new Date().toISOString(),
          fg_received_by: user.id,
        })
        .eq('id', request.work_order_id);

      toast({
        title: 'Success',
        description: `FG Receipt ${receiptNumber} created successfully`,
      });

      return receipt;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create FG receipt';
      setError(errorMessage);
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [user, toast]);

  // Update QC status
  const updateQCStatus = useCallback(async (
    id: string,
    qcStatus: 'PENDING' | 'PASSED' | 'FAILED' | 'PARTIAL',
    passedQty?: number,
    rejectedQty?: number
  ): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);

      const updateData: Record<string, unknown> = {
        qc_status: qcStatus,
        updated_at: new Date().toISOString(),
      };

      if (passedQty !== undefined) {
        updateData.qc_passed_qty = passedQty;
      }
      if (rejectedQty !== undefined) {
        updateData.qc_rejected_qty = rejectedQty;
      }

      const { error: updateError } = await supabase
        .from('finished_goods_receipt')
        .update(updateData)
        .eq('id', id);

      if (updateError) throw updateError;

      toast({
        title: 'Success',
        description: 'QC status updated successfully',
      });

      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update QC status';
      setError(errorMessage);
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // Fetch warehouses (for dropdown)
  const fetchWarehouses = useCallback(async (type?: string): Promise<Warehouse[]> => {
    try {
      let query = supabase
        .from('warehouse')
        .select('*')
        .eq('is_active', true)
        .order('name');

      if (type) {
        query = query.eq('type', type);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;
      return data || [];
    } catch (err) {
      console.error('Failed to fetch warehouses:', err);
      return [];
    }
  }, []);

  // Get FG warehouse specifically
  const getFGWarehouse = useCallback(async (): Promise<Warehouse | null> => {
    try {
      const { data, error: fetchError } = await supabase
        .from('warehouse')
        .select('*')
        .eq('type', 'FG')
        .eq('is_active', true)
        .single();

      if (fetchError) throw fetchError;
      return data;
    } catch (err) {
      console.error('Failed to fetch FG warehouse:', err);
      return null;
    }
  }, []);

  return {
    loading,
    error,
    fetchFGReceipts,
    getFGReceipt,
    getWorkOrderFGSummary,
    createFGReceipt,
    updateQCStatus,
    fetchWarehouses,
    getFGWarehouse,
  };
}
